import { Fragment, Suspense, useEffect, useRef, useState } from 'react';
import {
  IonButtons,
  IonContent,
  IonHeader,
  IonModal,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonRouter,
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { getActivities, getActivityCount } from '../../services/activityService';
import {
  useMarkActivitiesRead,
  useMarkActivitiesUnread,
  useMarkAllActivitiesRead,
} from '../../hooks/useActivities';
import { notify } from '../../services/notify';
import { useProfileStore } from '../../store/useProfileStore';
import { useActivityListStore } from '../../store/useActivityListStore';
import { useActivitySheetStore } from '../../store/useActivitySheetStore';
import { ActivityOrderCard } from './ActivityOrderCard';
import { ActivityConnectionCard } from './ActivityConnectionCard';
import { ActivityListingStatusCard } from './ActivityListingStatusCard';
import { SnoozeChooserSheet } from './SnoozeChooserSheet';
import { MarkReadButton } from './MarkReadButton';
import { SwipeToRead } from './SwipeToRead';
import { DishInfoCard } from '../listings/DishInfoCard';
import type { RecipientActivity } from '../../types/activity';
import type { TimeWindow } from '../../types/listing';

interface ActivityListModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FETCH_SIZE = 20;

/** Swipe is invisible until it's been discovered once, so the list carries a
 *  one-line coach until the user marks something read by any means. Persisted
 *  so it doesn't reappear on every open. */
const SWIPE_HINT_KEY = 'hp.activity.swipeHintSeen';

/** Undo window on a mark-read toast. Longer than the 3s default: undo is only
 *  useful if it's still there when the user realises they cleared the wrong
 *  card, and re-finding it in the Read tab is the fallback either way. */
const UNDO_TOAST_MS = 7000;

function hasSeenSwipeHint(): boolean {
  try {
    return localStorage.getItem(SWIPE_HINT_KEY) === '1';
  } catch {
    // Private-mode / blocked storage: show the hint, just don't remember.
    return false;
  }
}

function rememberSwipeHintSeen(): void {
  try {
    localStorage.setItem(SWIPE_HINT_KEY, '1');
  } catch {
    /* nothing to do — the hint simply returns next session */
  }
}

function getOrderMeta(activity: RecipientActivity): Record<string, unknown> | undefined {
  return (activity.metadata as Record<string, unknown> | undefined)?.order as
    | Record<string, unknown>
    | undefined;
}

function getConnectionMeta(activity: RecipientActivity): Record<string, unknown> | undefined {
  return (activity.metadata as Record<string, unknown> | undefined)?.connection as
    | Record<string, unknown>
    | undefined;
}

// Listing-lifecycle activities (listing went live / ended) carry `listing`
// metadata mirroring the `listing*`/`lister*` keys on order activities, so they
// group under the same DishInfoCard header as that listing's orders.
function getListingMeta(activity: RecipientActivity): Record<string, unknown> | undefined {
  return (activity.metadata as Record<string, unknown> | undefined)?.listing as
    | Record<string, unknown>
    | undefined;
}

function getOrderId(activity: RecipientActivity): string {
  return getOrderMeta(activity)?.orderId as string;
}

interface OrderGroup {
  listingId: string;
  listing: {
    title: string;
    imageUrl?: string;
    thumbnailUrl?: string;
    timeWindow?: TimeWindow;
    // Per-method windows. When present the header shows each hand-off's own
    // times; `timeWindow` is the envelope spanning both and only a fallback
    // for activities written before these were added.
    pickupWindow?: TimeWindow;
    dropoffWindow?: TimeWindow;
    listerSummary?: { id: string; displayName?: string; avatarUrl?: string };
  };
  // Listing-lifecycle cards (listing went live / ended) — pinned above the
  // order cards under the same DishInfoCard header. Newest-first (activities
  // arrive pre-sorted), so "listing ended" sits at the very top.
  listingStatusActivities: RecipientActivity[];
  activities: RecipientActivity[];
}

/**
 * `listingFallback` is the translated string injected when an activity's
 * order metadata is missing `listingTitle`. Threaded in from the parent
 * so the helper stays pure (no i18next dependency) and the parent owns
 * the `useTranslation` binding.
 */
function groupActivities(activities: RecipientActivity[], activeProfileId: string | undefined, listingFallback: string) {
  const connections: RecipientActivity[] = [];
  const nonOrder: RecipientActivity[] = [];
  const ordererOrders: RecipientActivity[] = [];
  const ungrouped: RecipientActivity[] = [];
  const groupMap = new Map<string, OrderGroup>();

  // Find-or-create the group for a listing, filling any header fields not yet
  // known. Order metadata is the richest source (time window + lister); a
  // listing-lifecycle card seeds title/image and, when it lands first, the
  // order pass upgrades the rest — so the header is complete regardless of
  // which activity type is encountered first.
  const ensureGroup = (
    listingId: string,
    header: Partial<OrderGroup['listing']>,
  ): OrderGroup => {
    let g = groupMap.get(listingId);
    if (!g) {
      g = {
        listingId,
        listing: { title: header.title ?? listingFallback },
        listingStatusActivities: [],
        activities: [],
      };
      groupMap.set(listingId, g);
    }
    // Fill missing header fields (first non-empty value per field wins).
    if ((!g.listing.title || g.listing.title === listingFallback) && header.title) {
      g.listing.title = header.title;
    }
    if (!g.listing.imageUrl && header.imageUrl) g.listing.imageUrl = header.imageUrl;
    if (!g.listing.thumbnailUrl && header.thumbnailUrl) g.listing.thumbnailUrl = header.thumbnailUrl;
    if (!g.listing.timeWindow && header.timeWindow) g.listing.timeWindow = header.timeWindow;
    if (!g.listing.pickupWindow && header.pickupWindow) g.listing.pickupWindow = header.pickupWindow;
    if (!g.listing.dropoffWindow && header.dropoffWindow) g.listing.dropoffWindow = header.dropoffWindow;
    if (!g.listing.listerSummary && header.listerSummary) g.listing.listerSummary = header.listerSummary;
    return g;
  };

  // activities come pre-sorted newest-first; insertion order into groupMap
  // therefore matches "most-recent activity per listing" — exactly the section
  // sort order we want, and stable across cursor pagination.
  for (const a of activities) {
    const orderMeta = getOrderMeta(a);
    if (orderMeta) {
      const listerId = orderMeta.listerId as string | undefined;
      // Recipient is the orderer (or recipient) when the listing's lister isn't
      // them — render those inline with their own DishInfoCard header rather
      // than grouping, since one orderer typically has one order per listing.
      if (!listerId || listerId !== activeProfileId) {
        ordererOrders.push(a);
        continue;
      }
      const listingId = orderMeta.listingId as string | undefined;
      if (!listingId) {
        ungrouped.push(a);
        continue;
      }
      const g = ensureGroup(listingId, {
        title: orderMeta.listingTitle as string | undefined,
        imageUrl: orderMeta.listingImageUrl as string | undefined,
        thumbnailUrl: orderMeta.listingThumbnailUrl as string | undefined,
        timeWindow: orderMeta.listingTimeWindow as TimeWindow | undefined,
        pickupWindow: orderMeta.listingPickupWindow as TimeWindow | undefined,
        dropoffWindow: orderMeta.listingDropoffWindow as TimeWindow | undefined,
        listerSummary: {
          id: listerId,
          displayName: orderMeta.listerDisplayName as string | undefined,
          avatarUrl: orderMeta.listerAvatarUrl as string | undefined,
        },
      });
      g.activities.push(a);
      continue;
    }

    // Listing-lifecycle cards (listing went live / ended) group under the
    // listing's DishInfoCard header, pinned above its orders — with or without
    // any orders present, so an ended listing always surfaces its own card.
    const listingMeta = getListingMeta(a);
    if (listingMeta) {
      const listingId = listingMeta.listingId as string | undefined;
      if (!listingId) {
        nonOrder.push(a);
        continue;
      }
      const listerId = listingMeta.listerId as string | undefined;
      const g = ensureGroup(listingId, {
        title: listingMeta.listingTitle as string | undefined,
        imageUrl: listingMeta.listingImageUrl as string | undefined,
        thumbnailUrl: listingMeta.listingThumbnailUrl as string | undefined,
        timeWindow: listingMeta.listingTimeWindow as TimeWindow | undefined,
        pickupWindow: listingMeta.listingPickupWindow as TimeWindow | undefined,
        dropoffWindow: listingMeta.listingDropoffWindow as TimeWindow | undefined,
        listerSummary: listerId
          ? {
              id: listerId,
              displayName: listingMeta.listerDisplayName as string | undefined,
              avatarUrl: listingMeta.listerAvatarUrl as string | undefined,
            }
          : undefined,
      });
      g.listingStatusActivities.push(a);
      continue;
    }

    // Connection activities get their own actionable card; everything else
    // falls back to the generic notification card.
    if (getConnectionMeta(a)) {
      connections.push(a);
    } else {
      nonOrder.push(a);
    }
  }

  return { connections, nonOrder, groups: Array.from(groupMap.values()), ordererOrders, ungrouped };
}

interface OrderGroupSectionProps {
  group: OrderGroup;
  onListingClick: () => void;
  renderOrderCard: (activity: RecipientActivity) => React.ReactNode;
  renderListingStatusCard: (activity: RecipientActivity) => React.ReactNode;
}

const OrderGroupSection: React.FC<OrderGroupSectionProps> = ({
  group,
  onListingClick,
  renderOrderCard,
  renderListingStatusCard,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [showStickyTitle, setShowStickyTitle] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyTitle(
          !entry.isIntersecting && entry.boundingClientRect.top < 0,
        );
      },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="relative flex flex-col">
      <div ref={cardRef}>
        <DishInfoCard
          listing={{
            title: group.listing.title,
            imageUrl: group.listing.imageUrl,
            thumbnailUrl: group.listing.thumbnailUrl,
            timeWindow: group.listing.timeWindow ?? null,
            pickupWindow: group.listing.pickupWindow ?? null,
            dropoffWindow: group.listing.dropoffWindow ?? null,
            fulfillmentMethod: [
              ...(group.listing.pickupWindow ? (['pickup'] as const) : []),
              ...(group.listing.dropoffWindow ? (['dropoff'] as const) : []),
            ],
            listerSummary: group.listing.listerSummary,
          }}
          onClick={onListingClick}
          disableExpand
        />
      </div>

      <div
        className={[
          'sticky top-0 z-10 -mx-6 px-6 transition-all duration-150',
          showStickyTitle
            ? 'py-3 bg-background/95 backdrop-blur-sm border-b border-border opacity-100'
            : 'py-0.5 opacity-0 pointer-events-none',
        ].join(' ')}
        aria-hidden={!showStickyTitle}
      >
        <h3
          className={[
            'font-heading font-semibold truncate text-foreground transition-all duration-150',
            showStickyTitle ? 'text-lg leading-snug' : 'text-xs leading-tight',
          ].join(' ')}
        >
          {group.listing.title}
        </h3>
      </div>

      <div className="flex flex-col gap-3">
        {group.listingStatusActivities.map(renderListingStatusCard)}
        {group.activities.map(renderOrderCard)}
      </div>
    </div>
  );
};

async function loadData(profileId: string, status: 'new' | 'read', cursor?: string) {
  const store = useActivityListStore.getState();
  store.setLoading(true);
  try {
    const [countsNew, countsRead, results] = await Promise.all([
      getActivityCount(profileId, 'new'),
      getActivityCount(profileId, 'read'),
      getActivities(profileId, status, FETCH_SIZE, cursor),
    ]);
    useActivityListStore.getState().setUnreadCount(countsNew.count);
    useActivityListStore.getState().setReadCount(countsRead.count);
    if (cursor) {
      useActivityListStore.getState().appendActivities(results);
    } else {
      useActivityListStore.getState().setActivities(results);
    }
    useActivityListStore.getState().setHasMore(results.length === FETCH_SIZE);
    useActivityListStore.getState().setCursorId(results.length > 0 ? results[results.length - 1].id : undefined);
  } finally {
    useActivityListStore.getState().setLoading(false);
  }
}

// Hoisted so the array keeps a stable reference across re-renders. The modal
// re-renders several times in quick succession while loadData runs on open;
// passing a fresh `breakpoints` array each time makes Ionic re-init the sheet
// gesture mid-animation and snap it closed.
const SHEET_BREAKPOINTS = [0, 0.6, 0.9];

/**
 * Inner content of the activity sheet. Split out from ActivityListModal so the
 * shell's Suspense boundary wraps EVERY translation-using node: this component
 * calls useTranslation('activity'), and its order cards render OrderCard which
 * calls useTranslation('orders'). Both namespaces lazy-load, and with
 * `react.useSuspense: true` the first render suspends mid-tree. Caught by the
 * shell's boundary (inside the still-mounted IonModal) it shows a spinner;
 * uncaught it bubbles to the root `fallback={null}` boundary in main.tsx,
 * blanks the whole app, and rips the open modal out of the DOM with no dismiss
 * event — the flash-then-vanish bug.
 */
const ActivityListContent: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t, i18n } = useTranslation('activity');
  const router = useIonRouter();
  const { activities, unreadCount, readCount, loading, hasMore, cursorId, view } = useActivityListStore();
  const { setView, setActivities, setCursorId } = useActivityListStore.getState();
  const scrollTargetOrderId = useActivitySheetStore((s) => s.scrollTargetOrderId);
  const clearScrollTarget = useActivitySheetStore((s) => s.clearScrollTarget);
  const markRead = useMarkActivitiesRead();
  const markUnread = useMarkActivitiesUnread();
  const markAllRead = useMarkAllActivitiesRead();
  const [showSwipeHint, setShowSwipeHint] = useState(() => !hasSeenSwipeHint());

  // When a toast sets the scroll target while the sheet is ALREADY open, reload
  // so a freshly-arrived order appears in the list — then the matching card's
  // own auto-scroll effect takes over. The first run is skipped: a tap-open sets
  // the target before mount, and onDidPresent already loads that case (avoids a
  // double fetch). Cleared targets (null) are no-ops.
  // Which chef's new-dish "Snooze" chooser is open (null = closed). Set from a
  // connection-dish listing-status card; consumed by SnoozeChooserSheet below.
  const [snoozeTarget, setSnoozeTarget] = useState<{ listerId: string; listerName?: string } | null>(null);

  const firstScrollTargetRun = useRef(true);
  useEffect(() => {
    if (firstScrollTargetRun.current) {
      firstScrollTargetRun.current = false;
      return;
    }
    if (!scrollTargetOrderId) return;
    const profileId = useProfileStore.getState().activeProfile?.id;
    if (!profileId) return;
    // Targets are always unread items, and a tap-from-closed lands on "new";
    // match that so the card is in view regardless of the current tab.
    setView('new');
    loadData(profileId, 'new');
  }, [scrollTargetOrderId, setView]);

  const handleViewChange = (newView: 'new' | 'read') => {
    const profileId = useProfileStore.getState().activeProfile?.id;
    if (!profileId) return;
    setView(newView);
    setActivities([]);
    setCursorId(undefined);
    loadData(profileId, newView);
  };

  const handleLoadMore = () => {
    const profileId = useProfileStore.getState().activeProfile?.id;
    if (!profileId) return;
    loadData(profileId, view, cursorId);
  };

  const dismissSwipeHint = () => {
    rememberSwipeHintSeen();
    setShowSwipeHint(false);
  };

  /**
   * Mark `activityIds` read, reload, and raise a toast whose Undo puts exactly
   * that set back. Shared by the per-card button, the swipe gesture and Mark
   * all as read, so all three are equally reversible — which is what makes the
   * bulk action safe to offer without a confirm dialog.
   */
  const markReadWithUndo = async (activityIds: string[], message: string) => {
    const profileId = useProfileStore.getState().activeProfile?.id;
    if (!profileId || activityIds.length === 0) return;
    await markRead.mutateAsync({ profileId, activityIds });
    await loadData(profileId, view);
    // Marking anything read means they found an affordance; the coach line has
    // done its job whichever one they used.
    dismissSwipeHint();
    notify.success(message, {
      // Fixed id so a rapid series of dismissals replaces one toast instead of
      // stacking a column of them. Only the newest is undoable, which matches
      // what the single visible toast claims.
      id: 'activity-marked-read',
      duration: UNDO_TOAST_MS,
      action: {
        label: t('undo'),
        onClick: () => {
          markUnread.mutate(
            { profileId, activityIds },
            { onSuccess: () => loadData(profileId, view) },
          );
        },
      },
    });
  };

  const handleMarkAsRead = (activityId: string) => {
    void markReadWithUndo([activityId], t('toast.markedRead'));
  };

  const handleMarkAllRead = () => {
    const profileId = useProfileStore.getState().activeProfile?.id;
    if (!profileId) return;
    markAllRead.mutate(profileId, {
      onSuccess: async (ids) => {
        await loadData(profileId, view);
        if (ids.length === 0) return;
        dismissSwipeHint();
        notify.success(t('toast.markedAllRead', { count: ids.length }), {
          id: 'activity-marked-read',
          duration: UNDO_TOAST_MS,
          action: {
            label: t('undo'),
            onClick: () => {
              markUnread.mutate(
                { profileId, activityIds: ids },
                { onSuccess: () => loadData(profileId, view) },
              );
            },
          },
        });
      },
      onError: () => notify.error(t('toast.markAllFailed')),
    });
  };

  const handleRoute = (route: string) => {
    onClose();
    router.push(route, 'forward');
  };

  const activeProfileId = useProfileStore.getState().activeProfile?.id;

  const renderOrderCard = (activity: RecipientActivity, showListingHeader = false) => {
    const orderId = getOrderId(activity);
    return (
      <ActivityOrderCard
        key={activity.id}
        orderId={orderId}
        activityId={activity.id}
        onActivityRead={() => {
          const profileId = useProfileStore.getState().activeProfile?.id;
          if (profileId) loadData(profileId, view);
        }}
        onDismiss={handleMarkAsRead}
        onReviewed={onClose}
        onNavigate={onClose}
        showListingHeader={showListingHeader}
        autoScroll={!!scrollTargetOrderId && orderId === scrollTargetOrderId}
        onAutoScrolled={clearScrollTarget}
      />
    );
  };

  // Pinned listing-lifecycle card (listing went live / ended / a connection just
  // posted a dish), rendered at the top of its listing group under the
  // DishInfoCard header. A connection-dish card ("X just posted Y") shares this
  // renderer with listing_live/expired — all carry metadata.listing — and the
  // card itself reads the listing's live status so a dish that has since sold
  // out stamps itself and auto-reads instead of lingering unread forever (see
  // ActivityListingStatusCard). Anything still orderable stays put until the
  // user dismisses it (X).
  const renderListingStatusCard = (activity: RecipientActivity) => (
    <ActivityListingStatusCard
      key={activity.id}
      activity={activity}
      unread={view === 'new'}
      onDismiss={handleMarkAsRead}
      onSnooze={setSnoozeTarget}
    />
  );

  const { connections, nonOrder, groups, ordererOrders, ungrouped } = groupActivities(activities, activeProfileId, t('listingFallback'));

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <button
              type="button"
              onClick={onClose}
              className="px-4 text-sm font-medium text-brand-primary"
            >
              {t('modal.done')}
            </button>
          </IonButtons>
          <IonTitle className="ion-text-center" style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, pointerEvents: 'none' }}>{t('modal.title')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="p-6 flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleViewChange('new')}
              className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                view === 'new'
                  ? 'bg-brand-primary text-white'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              }`}
            >
              {t('tabs.unread', { count: unreadCount })}
            </button>
            <button
              type="button"
              onClick={() => handleViewChange('read')}
              className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                view === 'read'
                  ? 'bg-brand-primary text-white'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              }`}
            >
              {t('tabs.read', { count: readCount })}
            </button>

            {/* Only in Unread, and only when there's something to clear —
                otherwise it's a permanently disabled control taking up the row. */}
            {view === 'new' && unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                disabled={markAllRead.isPending}
                className="ml-auto flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-brand-primary transition-colors hover:bg-accent disabled:opacity-50"
              >
                {markAllRead.isPending && <IonSpinner name="crescent" className="h-4 w-4" />}
                {t('markAllRead')}
              </button>
            )}
          </div>

          {/* Swipe is undiscoverable on its own. One line, shown until the user
              marks anything read by any means, then never again. */}
          {showSwipeHint && view === 'new' && activities.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg bg-muted/60 px-3 py-2">
              <p className="flex-1 text-xs text-muted-foreground">{t('swipeHint')}</p>
              <button
                type="button"
                onClick={dismissSwipeHint}
                aria-label={t('swipeHintDismiss')}
                className="-mr-1 -mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                &times;
              </button>
            </div>
          )}

          <div className="flex flex-col gap-3">
            {loading && activities.length === 0 ? (
              <div className="flex justify-center py-8">
                <IonSpinner name="crescent" />
              </div>
            ) : activities.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {view === 'new' ? t('empty.unread') : t('empty.read')}
              </p>
            ) : (
              <>
                {connections.map((activity) => (
                  <ActivityConnectionCard
                    key={activity.id}
                    activity={activity}
                    unread={view === 'new'}
                    onMarkRead={handleMarkAsRead}
                    onRoute={handleRoute}
                  />
                ))}

                {nonOrder.map((activity) => (
                  <SwipeToRead
                    key={activity.id}
                    onCommit={() => handleMarkAsRead(activity.id)}
                    label={t('card.markAsRead')}
                    disabled={view !== 'new'}
                  >
                  <div
                    className="relative border border-border rounded-lg p-4"
                  >
                    {/* pr-9 clears the absolutely-positioned mark-read button;
                        without it the timestamp runs underneath it. */}
                    <div className="flex items-center justify-between gap-2 mb-1 pr-9">
                      <span className="text-xs font-medium text-muted-foreground uppercase">{activity.type}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(activity.createdAt).toLocaleString(i18n.language)}
                      </span>
                    </div>

                    <p className="text-sm font-medium mb-1">{activity.message}</p>

                    {activity.content && (
                      <div
                        className="text-sm text-muted-foreground mb-2"
                        dangerouslySetInnerHTML={{ __html: activity.content }}
                      />
                    )}

                    {activity.imageUrl && (
                      <img
                        src={activity.imageUrl}
                        alt=""
                        className="aspect-[4/3] w-full rounded-lg object-cover mb-2"
                      />
                    )}

                    {activity.displayProfiles?.length > 0 && (
                      <div className="flex items-center gap-2 mb-2">
                        {activity.displayProfiles.map((dp) => (
                          <div key={dp.profileId} className="flex items-center gap-1">
                            {dp.avatarUrl && (
                              <img
                                src={dp.avatarUrl}
                                alt={dp.displayName}
                                className="w-5 h-5 rounded-full object-cover"
                              />
                            )}
                            <span className="text-xs text-muted-foreground">{dp.displayName}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {activity.route && activity.route.length > 0 && (
                      <button
                        type="button"
                        onClick={() => handleRoute(activity.route!)}
                        className="text-sm text-brand-primary hover:underline"
                      >
                        {t('card.checkItOut')}
                      </button>
                    )}

                    {view === 'new' && (
                      <MarkReadButton
                        onClick={() => handleMarkAsRead(activity.id)}
                        label={t('card.markAsRead')}
                        className="absolute top-2 right-2"
                      />
                    )}
                  </div>
                  </SwipeToRead>
                ))}

                {groups.map((group, idx) => (
                  <Fragment key={group.listingId}>
                    {idx > 0 && (
                      <div
                        className="my-8 border-t border-border"
                        aria-hidden="true"
                      />
                    )}
                    <OrderGroupSection
                      group={group}
                      onListingClick={() => handleRoute(`/listing/${group.listingId}`)}
                      renderOrderCard={(activity) => renderOrderCard(activity)}
                      renderListingStatusCard={renderListingStatusCard}
                    />
                  </Fragment>
                ))}

                {ordererOrders.map((activity) => renderOrderCard(activity, true))}

                {ungrouped.map((activity) => renderOrderCard(activity))}
              </>
            )}

            {hasMore && !loading && (
              <div className="flex justify-center pt-2">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  className="text-sm text-brand-primary hover:underline"
                >
                  {t('loadMore')}
                </button>
              </div>
            )}

            {loading && activities.length > 0 && (
              <div className="flex justify-center py-2">
                <IonSpinner name="crescent" className="w-5 h-5" />
              </div>
            )}
          </div>
        </div>
      </IonContent>

      <SnoozeChooserSheet
        isOpen={snoozeTarget !== null}
        onClose={() => setSnoozeTarget(null)}
        listerId={snoozeTarget?.listerId ?? ''}
        listerName={snoozeTarget?.listerName}
      />
    </>
  );
};

export const ActivityListModal: React.FC<ActivityListModalProps> = ({
  isOpen,
  onClose,
}) => {
  // The data load is driven off onDidPresent (after the present animation
  // settles) so loadData's burst of store updates can't re-render the sheet
  // mid-animation. This shell deliberately does NOT subscribe to the store or
  // call useTranslation, so it — and the IonModal — stay stable no matter what
  // the content does; the Suspense boundary keeps a lazy namespace load from
  // tearing the open modal out of the DOM (see ActivityListContent).
  const handleDidPresent = () => {
    const profileId = useProfileStore.getState().activeProfile?.id;
    if (!profileId) return;
    useActivityListStore.getState().reset();
    loadData(profileId, 'new');
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidPresent={handleDidPresent}
      onDidDismiss={() => {
        useActivityListStore.getState().reset();
        onClose();
      }}
      breakpoints={SHEET_BREAKPOINTS}
      initialBreakpoint={0.9}
      handle
      // Lets IonContent scroll at the 0.75 breakpoint. With the default
      // (expandToScroll=true) Ionic disables content scroll at any non-max
      // breakpoint and the drag gesture owns vertical movement, so the list
      // only scrolls once dragged to full — which read as "can't scroll".
      expandToScroll={false}
    >
      <Suspense
        fallback={
          <IonContent>
            <div className="flex justify-center py-12">
              <IonSpinner name="crescent" />
            </div>
          </IonContent>
        }
      >
        <ActivityListContent onClose={onClose} />
      </Suspense>
    </IonModal>
  );
};
